import { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCompanyConfig } from '@/lib/use-company-config';
import { CompanyConfigPlaceholder } from '@/components/shared/CompanyConfigPlaceholder';
import GoNoGoBadge from '@/components/shared/GoNoGoBadge';
import { cn } from '@/lib/utils';

const API_ROOT =
  import.meta.env.VITE_API_URL || import.meta.env.VITE_INDEXER_API_URL || '';
const SEARCH_URL = API_ROOT
  ? `${API_ROOT.replace(/\/$/, '')}/api/search`
  : '/api/search';

interface IndexedOpportunity {
  id: number;
  source: string;
  identifier: string;
  title: string;
  description: string;
  agency: string;
  region: string;
  status: string;
  deadline: string | null;
  budget_amount: number | null;
  currency: string;
  source_url: string;
  last_synced_at: string;
}

type Tab = 'search' | 'compare' | 'added';

export default function GrantDiscovery() {
  const { companyId } = useParams<{ companyId: string }>();
  const { config, loading, error } = useCompanyConfig(companyId);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<IndexedOpportunity[]>([]);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('search');
  const [addedIds, setAddedIds] = useState<Set<number>>(new Set());

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`${SEARCH_URL}?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        setResults(data.results ?? []);
      }
    } catch (e) {
      console.error('Search failed:', e);
    } finally {
      setSearching(false);
    }
  }, [query]);

  if (loading || error || !config) {
    return (
      <CompanyConfigPlaceholder companyId={companyId} loading={loading} error={error} config={config} />
    );
  }

  const existingIds = new Set(config.programs.map((p) => p.id));

  const toggleAdd = (opp: IndexedOpportunity) => {
    setAddedIds((prev) => {
      const next = new Set(prev);
      if (next.has(opp.id)) {
        next.delete(opp.id);
      } else {
        next.add(opp.id);
      }
      return next;
    });
  };

  const isInRoadmap = (opp: IndexedOpportunity) => {
    return existingIds.has(opp.identifier);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Grant Discovery
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {config.company.name} — Finding new opportunities
            </p>
          </div>
          <div className="flex gap-3">
            {companyId && (
              <Link
                to={`/${companyId}/roadmap`}
                className="text-sm px-4 py-2 rounded-lg border hover:bg-gray-50 transition-colors"
              >
                Back to Roadmap
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
          {(['search', 'compare', 'added'] as Tab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                activeTab === tab
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900',
              )}
            >
              {tab === 'search' && 'Search'}
              {tab === 'compare' && 'Compare to Roadmap'}
              {tab === 'added' && `Added (${addedIds.size})`}
            </button>
          ))}
        </div>

        {/* Search Tab */}
        {activeTab === 'search' && (
          <div>
            <div className="flex gap-3 mb-6">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search indexed opportunities (e.g., 'electronic warfare', 'DARPA', 'antenna systems')"
                className="flex-1 px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
              <button
                onClick={handleSearch}
                disabled={searching}
                className="px-6 py-3 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>

            {results.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-gray-500">{results.length} results</p>
                {results.map((opp) => (
                  <OpportunityCard
                    key={opp.id}
                    opportunity={opp}
                    inRoadmap={isInRoadmap(opp)}
                    added={addedIds.has(opp.id)}
                    onToggleAdd={() => toggleAdd(opp)}
                  />
                ))}
              </div>
            )}

            {results.length === 0 && query && !searching && (
              <div className="text-center py-12 text-gray-400">
                No results found. Try different keywords.
              </div>
            )}
          </div>
        )}

        {/* Compare Tab */}
        {activeTab === 'compare' && config && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Programs currently in the {config.company.name} roadmap:
            </p>
            <div className="grid gap-3">
              {config.programs.map((p) => (
                <div
                  key={p.id}
                  className="bg-white border rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <h3 className="font-medium text-gray-900">{p.program_name}</h3>
                    <p className="text-sm text-gray-500">
                      {p.grantor} — {p.deadline.display}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-400">
                      {p.score?.composite ?? 'Unscored'}
                    </span>
                    <GoNoGoBadge value={p.go_no_go} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Added Tab */}
        {activeTab === 'added' && (
          <div>
            {addedIds.size === 0 ? (
              <div className="text-center py-12 text-gray-400">
                No opportunities added yet. Search and click "Add to Roadmap" to get started.
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500">
                  {addedIds.size} opportunities selected for roadmap addition.
                  These will need enrichment via the pipeline before appearing in the roadmap.
                </p>
                {results
                  .filter((r) => addedIds.has(r.id))
                  .map((opp) => (
                    <OpportunityCard
                      key={opp.id}
                      opportunity={opp}
                      inRoadmap={isInRoadmap(opp)}
                      added={true}
                      onToggleAdd={() => toggleAdd(opp)}
                    />
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function OpportunityCard({
  opportunity: opp,
  inRoadmap,
  added,
  onToggleAdd,
}: {
  opportunity: IndexedOpportunity;
  inRoadmap: boolean;
  added: boolean;
  onToggleAdd: () => void;
}) {
  const budget = opp.budget_amount
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: opp.currency || 'USD',
        maximumFractionDigits: 0,
      }).format(opp.budget_amount)
    : 'N/A';

  return (
    <div className="bg-white border rounded-lg p-4 hover:border-primary/30 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-gray-900 truncate">{opp.title}</h3>
            <span className="shrink-0 px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-500">
              {opp.source}
            </span>
            {inRoadmap && (
              <span className="shrink-0 px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">
                In Roadmap
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mb-2">
            {opp.agency} — {opp.identifier}
          </p>
          {opp.description && (
            <p className="text-sm text-gray-600 line-clamp-2">{opp.description}</p>
          )}
          <div className="flex gap-4 mt-2 text-xs text-gray-400">
            <span>Deadline: {opp.deadline ?? 'N/A'}</span>
            <span>Budget: {budget}</span>
            <span>Status: {opp.status}</span>
            <span>Region: {opp.region}</span>
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {opp.source_url && (
            <a
              href={opp.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs px-3 py-1.5 border rounded-md hover:bg-gray-50 text-center"
            >
              View Source
            </a>
          )}
          {!inRoadmap && (
            <button
              onClick={onToggleAdd}
              className={cn(
                'text-xs px-3 py-1.5 rounded-md text-center transition-colors',
                added
                  ? 'bg-primary text-white'
                  : 'border border-primary text-primary hover:bg-primary/5',
              )}
            >
              {added ? 'Added' : 'Add to Roadmap'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
